/**
 * The engine's decisions on the analyzer cells (CU-869fmm949). Pure: `DecisionMark` and the detail render them.
 *
 * The server (`/api/repricing/analyzer/decisions`) answers `{cells: [{id_site, pickup_date, lor, decisions}],
 * not_priced: [{id_site, pickup_date, lor, n}], unplaced}`, one row per decision as the Activity shows them.
 * plus `own: {provider_ids, site_ids}`, the account's identity.
 *
 * **Marks go only on the account's own price cells, never on a competitor's** (Curro, 2026-10-27, over UX D6):
 * - Web: the rows of its own sites (`site_ids`), for the decisions of that pick-up date and length;
 * - Web & Provider: the rows of its own brands (`provider_ids`) on the site whose search triggered the decision.
 * The detail still names the reference competitor. When no own row is drawn (filtered out, or none has an offer),
 * the decisions are counted in the "rows not shown" note.
 */
import { capKey } from '../../../../../../DsRepricing/Activity/@services/dateProtection.js'
import { sentStyleOf } from '../../../../../../DsRepricing/Activity/@services/sendStyle.js'

/** Most relevant first (decision 6 a, UX Obs. 11). */
export const PRIORITY = ['pms_error', 'tethered_pms_error', 'quarantined', 'capped', 'applied', 'tethered', 'no_price_change']

export const KIND_LABELS = {
	pms_error: 'PMS error', tethered_pms_error: 'Tethered error', quarantined: 'Quarantined', capped: 'Capped by limit',
	applied: 'Applied', tethered: 'Tethered', no_price_change: 'No price change',
}

/** Icon (Font Awesome name) and tone per kind: the ones of the Activity's EVENT_META, except Applied, which uses the
 *  diagonal trend arrows so it never reads as the cell's vertical market arrows (UX Obs. 11). */
export const KIND_META = {
	pms_error: { icon: 'circle-exclamation', tone: 'err' },
	tethered_pms_error: { icon: 'link-slash', tone: 'err' },
	quarantined: { icon: 'flask', tone: 'accent' },
	capped: { icon: 'arrow-down-to-line', tone: 'warn' },
	applied: { icon: 'arrow-trend-up', tone: 'ok' },
	tethered: { icon: 'link', tone: 'info' },
	no_price_change: { icon: 'minus', tone: 'neutral' },
}

export function iconOf(mark) {
	if (!mark) return null
	if (mark.kind === 'applied') return mark.direction === 'down' ? 'arrow-trend-down' : 'arrow-trend-up'
	return KIND_META[mark.kind]?.icon || 'circle-info'
}

const CAP_LABELS = {
	rank_cap: 'Rank cap', keep_position: 'Keep-position guard', never_below_competitor: 'Never below competitor',
	never_above_competitor: 'Never above competitor', markup_cap: 'Markup limit', price_limit: 'Price limit',
	date_protection: 'Date protection', category_consistency: 'Category consistency',
}

/** "2026-11-09" from "2026-11-09", "20261109", 20261109 or a timestamp. */
export function dayOf(value) {
	const digits = String(value ?? '').replace(/[^0-9]/g, '').slice(0, 8)
	return digits.length === 8 ? `${digits.slice(0, 4)}-${digits.slice(4, 6)}-${digits.slice(6, 8)}` : null
}

export function cellKey({ site, date, lor, provider = null }) {
	const base = `${Number(site)}|${dayOf(date)}|${Number(lor)}`
	return provider == null ? base : `${base}|${Number(provider)}`
}

export const isSim = d => d?.payload?.mode === 'dry_run'

const EMPTY = Object.freeze({ decisions: [], notPriced: 0 })
const ids = list => (list || []).map(Number).filter(n => !Number.isNaN(n))

/** The pick-up date x length of a cell, the key of Web's own-site rows. */
export const axisKey = ({ date, lor }) => `${dayOf(date)}|${Number(lor)}`

/** `{sites: {site|date|lor: cell}, axis: {date|lor: cell}, own: {providers, sites}}`, a cell being
 *  `{decisions, notPriced}`: by the site that triggered the decision, and by date x length. */
export function indexDecisions(response) {
	const sites = {}, axis = {}
	const slot = (map, key) => (map[key] = map[key] || { decisions: [], notPriced: 0 })
	for (const cell of response?.cells || []) {
		const place = { site: cell.id_site, date: cell.pickup_date, lor: cell.lor }
		for (const d of cell.decisions || []) {
			slot(sites, cellKey(place)).decisions.push(d)
			slot(axis, axisKey(place)).decisions.push(d)
		}
	}
	for (const n of response?.not_priced || []) {
		const place = { site: n.id_site, date: n.pickup_date, lor: n.lor }
		slot(sites, cellKey(place)).notPriced += Number(n.n) || 0
		slot(axis, axisKey(place)).notPriced += Number(n.n) || 0
	}
	return { sites, axis, own: { providers: ids(response?.own?.provider_ids), sites: ids(response?.own?.site_ids) } }
}

/** Whether a row is the account's own price: its own site (Web) or its own brand (Web & Provider). */
export function isOwnRow({ site, provider = null, byProvider = false }, own) {
	if (!own) return false
	return byProvider ? provider != null && own.providers.includes(Number(provider)) : site != null && own.sites.includes(Number(site))
}

/** What a cell holds: nothing on a competitor's row, in every mode. */
export function cellDecisions(index, { site, date, lor, provider = null, byProvider = false }) {
	if (!index?.own || site == null || !dayOf(date) || lor == null) return EMPTY
	if (!isOwnRow({ site, provider, byProvider }, index.own)) return EMPTY
	return (byProvider ? index.sites[cellKey({ site, date, lor })] : index.axis[axisKey({ date, lor })]) || EMPTY
}

const num = v => (v == null || v === '' ? null : Number(v))

/** ↗ / ↘ for an applied decision: the price after against the price before. */
export function directionOf(d) {
	const before = num(d?.previous_price), after = num(d?.pushed_price ?? d?.new_price)
	if (before == null || after == null || before === after) return null
	return after > before ? 'up' : 'down'
}

/** The cell's mark: the most relevant kind, the total, SIM when every decision is simulated. Null without decisions. */
export function markOf(decisions) {
	if (!decisions?.length) return null
	const kind = PRIORITY.find(k => decisions.some(d => d.event_type === k)) || decisions[0].event_type
	const first = decisions.find(d => d.event_type === kind)
	const total = decisions.length
	const sim = decisions.every(isSim)
	return {
		kind, total, sim, direction: kind === 'applied' ? directionOf(first) : null,
		label: `${total} repricing ${total === 1 ? 'decision' : 'decisions'} · most relevant: ${KIND_LABELS[kind] || kind}${sim ? ' · simulated' : ''}`,
	}
}

/** The decisions of the detail, most relevant first. */
export function sortDecisions(decisions) {
	const rank = d => { const i = PRIORITY.indexOf(d.event_type); return i < 0 ? PRIORITY.length : i }
	return [...(decisions || [])].sort((a, b) => rank(a) - rank(b))
}

/** "€38.20 → €35.90 /day" (per day always, as the Activity: UX Obs. 15). */
export function priceLine(d, currency = '€') {
	const before = num(d?.previous_price), after = num(d?.pushed_price ?? d?.new_price)
	const fmt = v => `${currency}${v.toFixed(2)}`
	if (before == null && after == null) return null
	if (before == null) return `→ ${fmt(after)} /day`
	if (after == null) return `${fmt(before)} /day`
	return `${fmt(before)} → ${fmt(after)} /day`
}

/** The result chip, `{label, tone}` in the Activity's words (`sendStyle.js`, shared): "capped", "late · not applied",
 *  "not converted"…; a simulated decision is never "sent" (criterion 7). Null when the decision has no send at all
 *  (a quarantine, no price change), as the Activity shows no chip there. */
export function resultOf(d) {
	const status = isSim(d) ? 'dry_run' : d?.write_status
	return status ? sentStyleOf(status) : null
}

/** The limit that acted (the last cap of the chain), or null. */
export function limitOf(d) {
	const chain = Array.isArray(d?.cap_chain) ? d.cap_chain : []
	const cap = chain[chain.length - 1]
	return cap ? (CAP_LABELS[capKey(cap)] || cap.reason || null) : null
}

/** "Tethered from CDMR (parent decision, +€4.00)" or null. The engine writes the snapshot
 *  `{tether_name, dimension, parent_value, parent_price, child_value, delta: {k, v}}` (pms-adapter
 *  `tethering.py:104-109`); the Activity also reads the parent's own row at `parent_snapshot.scope.sipp`. */
export function tetherOf(d) {
	const via = d?.via_tether
	if (!via) return null
	const snap = via.parent_snapshot || {}
	const parent = snap.scope?.sipp || via.parent_value || snap.parent_value || null
	const delta = via.delta || snap.delta
	let spread = ''
	if (delta && delta.v != null && !Number.isNaN(Number(delta.v))) {
		const v = Number(delta.v)
		spread = delta.k === 'pct' ? `${v >= 0 ? '+' : '−'}${Math.abs(v)}%` : `${v >= 0 ? '+' : '−'}€${Math.abs(v).toFixed(2)}`
	}
	return parent
		? `Tethered from ${parent} (parent decision${spread ? `, ${spread}` : ''})`
		: `Tethered from a parent decision${spread ? ` (${spread})` : ''}`
}

/** The offer's price per day: `daily_price` in Daily; in Total the engine only sends `price`, the total, so it is
 *  divided by the rental length (results_normalized `dao.py:181-183`). Like with like (criterion 5). */
export function offerDailyPrice(priceData, lor) {
	const daily = num(priceData?.daily_price)
	if (daily != null) return daily
	const total = num(priceData?.price)
	const days = num(priceData?.length_of_rental ?? lor)
	return total != null && days ? total / days : null
}

/** "Reference: Cicar €41.60 /day", and whether that is the offer of this cell (criterion 5), compared per day. */
export function referenceOf(d, priceData, currency = '€', lor = null) {
	const ref = d?.reference
	if (!ref) return null
	const price = num(ref.daily_price)
	const text = `Reference: ${ref.provider || 'competitor'}${price != null ? ` ${currency}${price.toFixed(2)} /day` : ''}`
	const offerPrice = offerDailyPrice(priceData, lor)
	const isThisOffer = ref.id_master_provider != null && Number(priceData?.id_master_provider) === Number(ref.id_master_provider)
		&& (price == null || offerPrice == null || Math.abs(offerPrice - price) < 0.01)
	return { text, isThisOffer }
}

/** "Open in Activity" of one decision: the pass, its office, ACRISS, date and length (criterion 6, UX Obs. 16). */
export function activityHref(d, activityPath = '/ds-repricing/activity') {
	const scope = d?.scope || {}
	const params = new URLSearchParams()
	if (scope.execution_id) params.set('execution_id', scope.execution_id)
	if (scope.office) params.set('office', scope.office)
	if (scope.sipp) params.set('sipp', scope.sipp)
	const day = dayOf(scope.pickup_date)
	if (day) { params.set('pickup_from', day); params.set('pickup_to', day) }
	if (scope.duration_days != null) params.set('duration', String(scope.duration_days))
	return `${activityPath}?${params.toString()}`
}

/** "All in Activity" of a cell: the pass, its office and site, the date and the length — so the Activity's rows are
 *  this cell's N (criterion 8). `office` is the decisions' own (the PMS code the Activity filters by). */
export function cellActivityHref({ executionId, office, site, date, lor }, activityPath = '/ds-repricing/activity') {
	const params = new URLSearchParams({ execution_id: executionId })
	if (office) params.set('office', office)
	if (site != null) params.set('source', String(site))
	const day = dayOf(date)
	if (day) { params.set('pickup_from', day); params.set('pickup_to', day) }
	if (lor != null) params.set('duration', String(lor))
	return `${activityPath}?${params.toString()}`
}

/** Decisions on no drawn own row (filtered out, no own offer that day, or no identity), for the note above the table.
 *  `visibleCells`: the drawn cells, `[{site, provider, date, lor}]`. */
export function hiddenCount(index, visibleCells, { byProvider = false } = {}) {
	if (!index?.sites) return 0
	const shown = new Set()
	for (const c of visibleCells || []) {
		if (!isOwnRow({ ...c, byProvider }, index.own)) continue
		shown.add(byProvider ? cellKey({ site: c.site, date: c.date, lor: c.lor }) : axisKey(c))
	}
	let n = 0
	for (const [key, cell] of Object.entries(index.sites)) {
		const [site, day, lor] = key.split('|')
		const placedAt = byProvider ? key : axisKey({ date: day, lor })
		if (!shown.has(placedAt)) n += cell.decisions.length
	}
	return n
}
